import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Modal, Popconfirm, Table, Tag, Typography, message } from 'antd';
import { listVersions, restoreFromBackup } from '../../services/priceSheetsApi';

const { Text } = Typography;

const VersionHistoryModal = ({ sheetKey, displayName, onClose, onChanged }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);

  const load = useCallback(async () => {
    if (!sheetKey) return;
    setLoading(true);
    try {
      const data = await listVersions(sheetKey);
      setVersions(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load version history');
    } finally {
      setLoading(false);
    }
  }, [sheetKey]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restoreFromBackup(sheetKey);
      message.success('Restored from backup as a new version');
      await load();
      onChanged();
    } catch (err) {
      message.error(err.message || 'Could not restore from backup');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Modal title={displayName ? `Version History — ${displayName}` : 'Version History'} open={Boolean(sheetKey)} onCancel={onClose} width={640} footer={null} destroyOnClose>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Text type="secondary">
        Every past version stays retrievable — a re-upload never overwrites an old one. If the current version's
        file was accidentally deleted, restore always creates a new version from the automatic backup copy; it
        never mutates an existing row.
      </Text>
      <Table
        style={{ marginTop: 16 }}
        size="small"
        rowKey="version_id"
        loading={loading}
        dataSource={versions}
        pagination={false}
        locale={{ emptyText: <Empty description="No versions yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        columns={[
          {
            title: 'Version',
            dataIndex: 'version_number',
            render: (v, row) => (
              <>
                v{v} {row.is_current && <Tag color="blue">current</Tag>}
              </>
            ),
          },
          { title: 'Uploaded By', dataIndex: 'uploaded_by', render: (v) => v || '—' },
          { title: 'Uploaded At', dataIndex: 'uploaded_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
          { title: 'Checksum', dataIndex: 'checksum', render: (v) => <Text code>{String(v || '').slice(0, 12)}</Text> },
        ]}
      />
      <Popconfirm
        title="Restore from backup?"
        description="Creates a new version from the automatic backup copy. Use this only if the current version is missing or corrupted."
        onConfirm={handleRestore}
      >
        <Button danger style={{ marginTop: 16 }} loading={restoring}>
          Restore From Backup
        </Button>
      </Popconfirm>
    </Modal>
  );
};

export default VersionHistoryModal;
